import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

// Phosphor Icons
import {
  SquaresFour,
  CheckCircle as CheckCirclePh,
  Users as UsersPh,
  Folder as FolderPh,
  Gear,
  SignOut,
  DotsThreeVertical,
  Circle as CirclePh,
  Flag as FlagPh,
  Plus,
  X as XPh,
  PencilSimple,
  Trash,
  Palette as PalettePh,
  DownloadSimple,
  File as FilePh,
  FilePdf,
  Image as ImagePh,
  Table as TablePh,
  FileDoc,
  CloudArrowUp,
  Calendar as CalendarPh,
  MapPin as MapPinPh,
  List as ListPh,
  Check as CheckPh,
  User as UserPh,
  Tag as TagPh,
  CaretDown,
  Wallet,
  HourglassMedium,
  Bug,
} from '@phosphor-icons/react';

export type IconName =
  | 'dashboard' | 'task-alt' | 'people' | 'folder' | 'settings' | 'logout'
  | 'more-vert' | 'dot' | 'flag' | 'add' | 'check-circle'
  | 'close' | 'edit' | 'delete' | 'color-lens' | 'download'
  | 'file' | 'pdf' | 'image' | 'table' | 'document'
  | 'delete-outline' | 'upload' | 'calendar' | 'location'
  | 'menu' | 'check' | 'person' | 'topic' | 'chevron-down' | 'wallet' | 'hourglass'
  | 'bug';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyIcon = React.ComponentType<any>;

const iconMap: Record<IconName, AnyIcon> = {
  'dashboard': SquaresFour,
  'task-alt': CheckCirclePh,
  'people': UsersPh,
  'folder': FolderPh,
  'settings': Gear,
  'logout': SignOut,
  'more-vert': DotsThreeVertical,
  'dot': CirclePh,
  'flag': FlagPh,
  'add': Plus,
  'check-circle': CheckCirclePh,
  'close': XPh,
  'edit': PencilSimple,
  'delete': Trash,
  'color-lens': PalettePh,
  'download': DownloadSimple,
  'file': FilePh,
  'pdf': FilePdf,
  'image': ImagePh,
  'table': TablePh,
  'document': FileDoc,
  'delete-outline': Trash,
  'upload': CloudArrowUp,
  'calendar': CalendarPh,
  'location': MapPinPh,
  'menu': ListPh,
  'check': CheckPh,
  'person': UserPh,
  'topic': TagPh,
  'chevron-down': CaretDown,
  'wallet': Wallet,
  'hourglass': HourglassMedium,
  'bug': Bug,
};

const fontSizeToPixels: Record<string, number> = {
  small: 20,
  medium: 24,
  large: 35,
};

interface AppIconProps {
  name: IconName;
  fontSize?: 'inherit' | 'small' | 'medium' | 'large';
  sx?: SxProps<Theme>;
  className?: string;
}

const AppIcon: React.FC<AppIconProps> = ({ name, fontSize = 'medium', sx, className }) => {
  const Icon = iconMap[name];

  if (!Icon) return null;

  const sxObj = (sx || {}) as Record<string, unknown>;
  let size: number | undefined;

  if (typeof sxObj.fontSize === 'number') {
    size = sxObj.fontSize;
  } else if (typeof sxObj.fontSize === 'string' && sxObj.fontSize.endsWith('px')) {
    size = parseInt(sxObj.fontSize);
  } else if (fontSize === 'inherit') {
    size = undefined;
  } else {
    size = fontSizeToPixels[fontSize] || 24;
  }

  const { fontSize: _fs, ...restSx } = sxObj;

  return (
    <Box
      component="span"
      className={className}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
        width: size ? size : '1em',
        height: size ? size : '1em',
        flexShrink: 0,
        ...restSx,
      }}
    >
      <Icon size={size || '1em'} weight="regular" />
    </Box>
  );
};

export default AppIcon;
